import { useState, useRef, useEffect, useMemo } from 'react';

/**
 * Custom hook that manages currency data for both country-level and site-level currencies.
 * Handles smart merging of server data with user changes, preventing overwrites of manual edits.
 * 
 * @param {Array} sourceData - Array of country/site objects from SOECountries
 * @returns {Object} Currency management state and handlers
 */
export const useCurrencyManager = (sourceData = []) => {
  // Currency data states
  const [countryCurrencyData, setCountryCurrencyData] = useState({});
  const [siteCurrencyData, setSiteCurrencyData] = useState({});
  const [originalCurrencyData, setOriginalCurrencyData] = useState({});
  const [originalSiteCurrencyData, setOriginalSiteCurrencyData] = useState({});
  
  // State to track initialization (this will trigger re-renders unlike refs)
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Tracking refs to prevent overwrites and multiple initializations
  const currencyInitializedRef = useRef(false);
  const userCurrencyChangesRef = useRef(new Set());
  
  // Initialize and update currency data when source data changes
  useEffect(() => {
    // Skip if source data is empty to avoid unnecessary processing
    if (!sourceData.length) {
      currencyInitializedRef.current = false;
      setIsInitialized(false);
      userCurrencyChangesRef.current.clear();
      return;
    }

    const newCountryCurrencyData = {};
    const newSiteCurrencyData = {};
    
    sourceData.forEach((country) => {
      // Country-level currency (when siteId === 1)
      if ((country.countryCodeISO3 === "Study" || country.siteId === 1) && (country.currency || country.exchangeRate)) {
        newCountryCurrencyData[country.siteGroupId] = {
          currency: country.currency || '',
          exchangeRate: country.exchangeRate ? country.exchangeRate.toString() : ''
        };
      }
      
      // Site-level currency (when siteId !== 1)
      if (country.siteId !== 1 && (country.currency || country.exchangeRate)) {
        const siteKey = `${country.siteGroupId}-${country.siteId}`;
        newSiteCurrencyData[siteKey] = {
          currency: country.currency || '',
          exchangeRate: country.exchangeRate ? country.exchangeRate.toString() : ''
        };
      }
    });
    
    // Check if there are actual changes before updating state
    const hasCountryChanges = JSON.stringify(newCountryCurrencyData) !== JSON.stringify(countryCurrencyData);
    const hasSiteChanges = JSON.stringify(newSiteCurrencyData) !== JSON.stringify(siteCurrencyData);
    
    // Update state if there are changes OR if this is the first initialization
    if (!currencyInitializedRef.current || hasCountryChanges || hasSiteChanges) {
      if (!currencyInitializedRef.current || hasCountryChanges) {
        // Smart merge: preserve user manual changes while updating server data
        const mergedCountryData = { ...newCountryCurrencyData };
        
        Object.keys(countryCurrencyData).forEach(countryId => {
          if (userCurrencyChangesRef.current.has(`country-${countryId}`)) {
            mergedCountryData[countryId] = countryCurrencyData[countryId];
          }
        });
        
        setCountryCurrencyData(mergedCountryData);
        
        // ALWAYS update original data to track current server state 
        setOriginalCurrencyData(newCountryCurrencyData);
      }
      
      if (!currencyInitializedRef.current || hasSiteChanges) {
        // Similar logic for site data
        const mergedSiteData = { ...newSiteCurrencyData };
        
        Object.keys(siteCurrencyData).forEach(siteKey => {
          if (userCurrencyChangesRef.current.has(`site-${siteKey}`)) {
            mergedSiteData[siteKey] = siteCurrencyData[siteKey];
          }
        });
        
        setSiteCurrencyData(mergedSiteData);
        
        // ALWAYS update original site data to track current server state
        setOriginalSiteCurrencyData(newSiteCurrencyData);
      }
      
      currencyInitializedRef.current = true;
      setIsInitialized(true); // Trigger re-render to update change calculations
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceData]); // countryCurrencyData and siteCurrencyData intentionally excluded but referenced in closure

  // Country-level currency handlers
  const handleCurrencyChange = (countryId, value) => {
    const formattedValue = value?.replace(/[^A-Za-z]/g, '')?.toUpperCase()?.slice(0, 3) || "";
    
    // Mark this country as manually changed by user
    userCurrencyChangesRef.current.add(`country-${countryId}`);
    setCountryCurrencyData(prev => ({
      ...prev,
      [countryId]: {
        ...prev[countryId],
        currency: formattedValue
      }
    }));
  };

  const handleExchangeRateChange = (countryId, value) => {
    const regex = /^\d*\.?\d{0,4}$/;
    if (regex.test(value) && (value === '' || parseFloat(value) >= 0)) {
      // Mark this country as manually changed by user
      userCurrencyChangesRef.current.add(`country-${countryId}`);
      setCountryCurrencyData(prev => ({
        ...prev,
        [countryId]: {
          ...prev[countryId],
          exchangeRate: value
        }
      }));
    }
  };

  // Site-level currency handlers
  const handleSiteCurrencyChange = (siteGroupId, siteId, value) => {
    const siteKey = `${siteGroupId}-${siteId}`;
    const formattedValue = value?.replace(/[^A-Za-z]/g, '')?.toUpperCase()?.slice(0, 3) || "";
    
    // Mark this site as manually changed by user
    userCurrencyChangesRef.current.add(`site-${siteKey}`);
    setSiteCurrencyData(prev => ({
      ...prev,
      [siteKey]: {
        ...prev[siteKey],
        currency: formattedValue
      }
    }));
  };

  const handleSiteExchangeRateChange = (siteGroupId, siteId, value) => {
    const siteKey = `${siteGroupId}-${siteId}`;
    const regex = /^\d*\.?\d{0,4}$/;
    if (regex.test(value) && (value === '' || parseFloat(value) >= 0)) {
      // Mark this site as manually changed by user
      userCurrencyChangesRef.current.add(`site-${siteKey}`);
      setSiteCurrencyData(prev => ({
        ...prev,
        [siteKey]: {
          ...prev[siteKey],
          exchangeRate: value
        }
      }));
    }
  };

  // Computed values to check if data has changed from original
  const hasCurrencyChanges = useMemo(() => {
    // Don't report changes if not initialized yet
    if (!isInitialized) {
      return false;
    }

    const currencyKeys = new Set([
      ...Object.keys(originalCurrencyData),
      ...Object.keys(countryCurrencyData)
    ]);

    for (const key of currencyKeys) {
      const original = originalCurrencyData[key] || {};
      const current = countryCurrencyData[key] || {};

      if (original.currency !== current.currency || 
          original.exchangeRate !== current.exchangeRate) {
        return true;
      }
    }
    return false;
  }, [originalCurrencyData, countryCurrencyData, isInitialized]);

  const hasSiteCurrencyChanges = useMemo(() => {
    // Don't report changes if not initialized yet
    if (!isInitialized) {
      return false;
    }

    const siteKeys = new Set([
      ...Object.keys(originalSiteCurrencyData),
      ...Object.keys(siteCurrencyData)
    ]);

    for (const key of siteKeys) {
      const original = originalSiteCurrencyData[key] || {};
      const current = siteCurrencyData[key] || {};

      if (original.currency !== current.currency || 
          original.exchangeRate !== current.exchangeRate) {
        return true;
      }
    }
    return false;
  }, [originalSiteCurrencyData, siteCurrencyData, isInitialized]);

  // Reset function to clear user changes and restore original values (for discarding changes)
  const resetUserChanges = () => {
    userCurrencyChangesRef.current.clear();
    // Restore original values from server data
    setCountryCurrencyData({ ...originalCurrencyData });
    setSiteCurrencyData({ ...originalSiteCurrencyData });
  };

  // Function to mark current values as "saved" (after successful backend save)
  const markAsSaved = () => {
    userCurrencyChangesRef.current.clear();
    // Update original data to match current data (since they're now saved in backend)
    setOriginalCurrencyData({ ...countryCurrencyData });
    setOriginalSiteCurrencyData({ ...siteCurrencyData });
  };

  // Get summary of current changes
  const getChangesSummary = () => ({
    userModifiedEntries: Array.from(userCurrencyChangesRef.current),
    hasCountryChanges: hasCurrencyChanges,
    hasSiteChanges: hasSiteCurrencyChanges,
    isInitialized
  });

  return {
    // Data states
    countryCurrencyData,
    siteCurrencyData,
    originalCurrencyData,
    originalSiteCurrencyData,
    
    // Handlers
    handleCurrencyChange,
    handleExchangeRateChange,
    handleSiteCurrencyChange,
    handleSiteExchangeRateChange,
    
    // Computed values
    hasCurrencyChanges,
    hasSiteCurrencyChanges,
    
    // Utility functions
    resetUserChanges,
    markAsSaved,
    getChangesSummary,
    
    // For debugging/testing
    _internal: {
      currencyInitializedRef,
      userCurrencyChangesRef
    }
  };
};

export default useCurrencyManager;
